/**
 * Cookie-level auth signals, shared by middleware, the server layout and the client.
 *
 * Edge-safe on purpose: middleware imports this, so nothing here may touch Prisma,
 * `next/headers`, or anything marked "server-only".
 *
 * Two signals, answering two different questions:
 *
 *   1. `hasSessionCookie` — "does this browser present an RA session cookie?" A presence
 *      check only; whether the session is still acceptable (revoked, dead grant) is decided
 *      in auth.config's jwt(). Used by middleware to redirect off protected routes before
 *      anything renders.
 *
 *   2. `ra_sso_skip_probe` — "skip the silent SSO probe on the very next page load."
 *
 * On (2): RA cannot read raauth's own SSO cookie (httpOnly, owned by the raauth service), so
 * the only way to learn that a user signed in on a sister site is to ASK, via the hidden
 * `prompt=none` iframe in SilentSsoCheck. There is no cookie RA can inspect to predict the
 * answer — an earlier attempt gated the probe on "has RA ever held a session here", which
 * fails for exactly the case that matters: a user whose only session was created on
 * jobs.restaurantassociation.com. So the probe runs on every page load, and this marker
 * exists for the single case where we already know the answer: the redirect that lands
 * immediately after a deliberate logout.
 *
 * ONE page load only. SilentSsoCheck deletes it the moment it reads it, and a short Max-Age
 * bounds the case where that never happens (JS disabled, load aborted). It deliberately does
 * NOT mean "already asked this browser session": a longer-lived marker suppressed the probe
 * for the rest of the browser session, so a user who logged out here, signed in on a sister
 * site, and came back in the same tab was never adopted — the tab-focus re-arm that was
 * supposed to cover that only fires when a tab regains focus, never on the fresh document a
 * same-tab navigation produces.
 *
 * Not a security boundary: non-httpOnly and user-forgeable. Forging it only suppresses a
 * probe, which costs the forger their own SSO adoption.
 */

export const SSO_SKIP_PROBE_COOKIE = "ra_sso_skip_probe"

/**
 * Safety net only. The marker is normally consumed by the first client render that sees it;
 * this bounds how long it can linger if that render never happens.
 */
const SSO_SKIP_PROBE_MAX_AGE = 60

/**
 * `ra_auth_mode` — "the OIDC interaction about to start was entered from Join Free, not Login."
 *
 * raauth owns the delegated interaction URL: it always sends the browser back to
 * `/unified/auth?uid=…` and keeps NOTHING else, so neither the `mode=signup` RA was linked
 * with nor the best-effort `screen_hint=signup` on the /oidc/auth request survives the round
 * trip — someone who clicked Join Free lands on the login form. This marker carries that one
 * bit across the hop, out of band, because there is no in-band channel to carry it in.
 *
 * ONE page load only, same discipline as SSO_SKIP_PROBE_COOKIE: AuthModeSync consumes it on
 * the delegated render and stamps `mode=signup` into the URL instead, so a reload stays on
 * signup while the next Login click can't be hijacked by a stale marker. The short Max-Age
 * bounds the case where that render never happens.
 *
 * `SameSite=Lax` is load-bearing, not incidental: the return leg is a top-level cross-site
 * redirect from raauth, which Lax allows and Strict would drop.
 *
 * Not a security boundary — non-httpOnly and user-forgeable. Forging it picks which of two
 * public forms renders.
 */
export const AUTH_MODE_COOKIE = "ra_auth_mode"

/** Bounds a marker whose consumer never ran (JS disabled, load aborted). The hop it covers is
 *  a machine-speed redirect chain with no user interaction, so this is already generous. */
const AUTH_MODE_MAX_AGE = 300

/** True when the interaction this page load belongs to was entered from a signup link. */
export function hasSignupModeCookie(cookies: CookieReader): boolean {
   return cookies.getAll().some((c) => c.name === AUTH_MODE_COOKIE && c.value === "signup")
}

/** `document.cookie` string that arms the marker, for OidcRedirect to set before it leaves. */
export function authModeSetCookie(host: string | null | undefined): string {
   return `${AUTH_MODE_COOKIE}=signup; ${authModeAttrs(host, AUTH_MODE_MAX_AGE).join("; ")}`
}

/** `document.cookie` string that clears the marker. Same `Domain` caveat as the probe marker
 *  above: without it the delete misses the domain-scoped production cookie entirely. */
export function authModeClearCookie(host: string | null | undefined): string {
   return `${AUTH_MODE_COOKIE}=; ${authModeAttrs(host, 0).join("; ")}`
}

function authModeAttrs(host: string | null | undefined, maxAge: number): string[] {
   const attrs = ["Path=/", `Max-Age=${maxAge}`, "SameSite=Lax"]
   if (onBrandDomain(host)) attrs.push("Domain=.restaurantassociation.com", "Secure")
   return attrs
}

/**
 * `ra_sso_synced` — "this browser rolled raauth's SSO session recently; skip the keep-alive."
 *
 * Solves the opposite problem from SSO_SKIP_PROBE_COOKIE. That one suppresses the ADOPT probe
 * (are we logged in somewhere else?). This one throttles the KEEP-ALIVE probe, which exists
 * because the two sessions in play age on different clocks:
 *
 *   - RA's own Auth.js cookie is re-issued on every session read, so it rolls forward
 *     indefinitely while the user browses, and its raauth tokens are renewed server-to-server.
 *   - raauth's SSO session only rolls when the BROWSER reaches raauth (oidc-provider re-saves
 *     it and re-stamps the cookie in lib/shared/session.js). Back-channel refresh never
 *     touches it.
 *
 * So a user who only ever browses RA keeps RA alive forever while the shared SSO session ages
 * out untouched — and the day it dies they are still logged into RA and logged out of every
 * sister site, with nothing on RA able to notice. A periodic `prompt=none` round trip is the
 * only thing that rolls it, because the authorization endpoint is one of just three provider
 * routes that run the session middleware at all.
 *
 * Deliberately a cookie and not a timestamp on the session: presence IS the answer, so there
 * is nothing to parse and no clock skew to reason about, and Max-Age does the expiry. The
 * staleness trap that killed the old adopt-probe cooldown does not apply — the worst case
 * here is that a keep-alive happens a few days later than it could have, whereas suppressing
 * an adopt probe meant a real session was never picked up.
 *
 * Not a security boundary: non-httpOnly and user-forgeable. Forging it only delays the
 * forger's own keep-alive.
 */
export const SSO_SYNC_COOKIE = "ra_sso_synced"

/**
 * How long a keep-alive is good for — the third number in the linked triple.
 *
 * A keep-alive only runs on a page load, so the last one can lag the user's last page view by
 * up to this long. That lag is why raauth's session needs headroom over RA's, and it is the
 * `+ 7d` term in the invariant stated at raauth's `ttl.Session` and again in auth.ts:
 *
 *     raauth ttl.Session (45d)  >  RA session.maxAge (30d)  +  this (7d)
 *
 * Raising this eats the margin directly. Raise `ttl.Session` first.
 */
const SSO_SYNC_MAX_AGE = 7 * 24 * 60 * 60

/** `document.cookie` string that records a completed keep-alive. Same `Domain` discipline as
 *  the markers above: on the brand domain sister sites share the registrable domain, and
 *  anywhere else a `Domain` attribute makes the browser drop the cookie outright. */
export function ssoSyncedSetCookie(host: string | null | undefined): string {
   const attrs = ["Path=/", `Max-Age=${SSO_SYNC_MAX_AGE}`, "SameSite=Lax"]
   if (onBrandDomain(host)) attrs.push("Domain=.restaurantassociation.com", "Secure")
   return `${SSO_SYNC_COOKIE}=1; ${attrs.join("; ")}`
}

/**
 * Request header stamped by middleware with the incoming pathname. Layouts don't receive
 * the pathname (only pages get `params`/`searchParams`), and a layout that needs to build a
 * `callbackUrl` has nothing else to build it from — hardcoding one means /profile/benefits
 * sends you back to /profile/edit after login.
 */
export const PATHNAME_HEADER = "x-ra-pathname"

/**
 * Auth.js session cookie base names. The real cookie may carry a `__Secure-`/`__Host-`
 * prefix and, when the JWT outgrows 4 KB, a `.0`/`.1`/… chunk suffix — so match on prefix,
 * not equality. Keep in sync with the clear-list in app/api/unified/logout/route.ts.
 */
const SESSION_COOKIE_BASES = ["authjs.session-token", "next-auth.session-token"]

function isSessionCookieName(name: string): boolean {
   const unprefixed = name.replace(/^(__Secure-|__Host-)/, "")
   return SESSION_COOKIE_BASES.some((base) => unprefixed === base || unprefixed.startsWith(`${base}.`))
}

/** Minimal shape satisfied by both `NextRequest.cookies` and `next/headers`' cookies(). */
type CookieReader = { getAll: () => { name: string; value: string }[] }

/** True when the request presents any (possibly chunked/prefixed) Auth.js session cookie. */
export function hasSessionCookie(cookies: CookieReader): boolean {
   return cookies.getAll().some((c) => isSessionCookieName(c.name) && c.value !== "")
}

/** True when this page load should skip its silent SSO probe (we just logged out). */
export function hasSsoSkipProbe(cookies: CookieReader): boolean {
   return cookies.getAll().some((c) => c.name === SSO_SKIP_PROBE_COOKIE && c.value !== "")
}

/**
 * On the brand domain the marker is scoped to the registrable domain so sister sites share
 * it. Anywhere else (localhost, previews) a `Domain` attribute would make the browser drop
 * the cookie outright, so it stays host-only.
 */
export function onBrandDomain(host: string | null | undefined): boolean {
   const hostname = (host ?? "").split(":")[0].toLowerCase()
   return hostname === "restaurantassociation.com" || hostname.endsWith(".restaurantassociation.com")
}

/** Cookie attributes for the marker. */
export function ssoSkipProbeCookieOptions(host: string | null | undefined) {
   const branded = onBrandDomain(host)
   return {
      name: SSO_SKIP_PROBE_COOKIE,
      path: "/",
      sameSite: "lax" as const,
      // Readable and deletable by client JS — SilentSsoCheck consumes it on read.
      httpOnly: false,
      secure: branded,
      maxAge: SSO_SKIP_PROBE_MAX_AGE,
      ...(branded ? { domain: ".restaurantassociation.com" } : {}),
   }
}

/**
 * `document.cookie` string that deletes the marker, for SilentSsoCheck to consume it.
 *
 * The `Domain` attribute is load-bearing: a delete only matches a cookie with the same
 * domain scope, so a host-only delete silently leaves the domain-scoped production cookie in
 * place — which is how a "one page load" marker turns into a browser-session one on the
 * brand domain and nowhere else.
 */
export function ssoSkipProbeClearCookie(host: string | null | undefined): string {
   const attrs = ["Path=/", "Max-Age=0", "SameSite=Lax"]
   if (onBrandDomain(host)) attrs.push("Domain=.restaurantassociation.com", "Secure")
   return `${SSO_SKIP_PROBE_COOKIE}=; ${attrs.join("; ")}`
}

/** Shape satisfied by `NextResponse.cookies`. */
type CookieWriter = {
   set: (opts: { name: string; value: string } & Record<string, unknown>) => unknown
}

/**
 * Suppress the probe on the next page load, from the server. Used after a deliberate logout:
 * we know for certain there is no raauth session left, so the page we redirect to shouldn't
 * ask. Consumed by the client on read — see SSO_SKIP_PROBE_COOKIE.
 */
export function setSsoSkipProbe(cookies: CookieWriter, host: string | null | undefined): void {
   cookies.set({ ...ssoSkipProbeCookieOptions(host), value: "1" })
}
